import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSerial } from '../hooks/useSerial';
import MinimalSettingsModal from '../components/minimal/SettingsModal';
import { SerialOptions } from '../types/serial';


const MinimalCLI: React.FC = () => {
    const { status, logs, connect, disconnect, send, clearLogs, showIpConsentModal, handleIpConsent } = useSerial();
    const [input, setInput] = useState('');
    const scrollRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isTestMode, setIsTestMode] = useState(false);

    const [commandHistory, setCommandHistory] = useState<string[]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [savedInput, setSavedInput] = useState('');

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [logs]);

    const handleSend = (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim()) return;
        if (input.trim() === 'abhinav' || status === 'connected') {
            setCommandHistory(prev => {
                const next = prev.filter(c => c !== input.trim());
                next.push(input.trim());
                return next.slice(-50);
            });
            setHistoryIndex(-1);
            setSavedInput('');
            send(input + '\n', clearLogs, 115200);
        }
        setInput('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (commandHistory.length === 0) return;
            const newIdx = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
            if (historyIndex === -1) setSavedInput(input);
            setHistoryIndex(newIdx);
            setInput(commandHistory[newIdx]);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (historyIndex === -1) return;
            if (historyIndex === commandHistory.length - 1) {
                setHistoryIndex(-1);
                setInput(savedInput);
            } else {
                const newIdx = historyIndex + 1;
                setHistoryIndex(newIdx);
                setInput(commandHistory[newIdx]);
            }
        }
    };

    const handleConnect = (options: SerialOptions, testMode: boolean) => {
        setIsTestMode(testMode);
        connect(options, testMode);
    };

    return (
        <div className="space-y-6 h-[70vh] flex flex-col relative">
            <Helmet>
                <title>CLI Tools | Abhinav Chinnusamy</title>
            </Helmet>

            <header className="flex-none">
                <h1 className="text-xl font-bold uppercase tracking-widest">/cli</h1>
                <p className="mt-2 text-sm text-gray-600 flex items-center gap-2 flex-wrap">
                    Hardware interface terminal. Status:
                    <span className={`font-mono ${status === 'connected' ? 'text-green-600' : 'text-gray-400'}`}>
                        [{status.toUpperCase()}]
                    </span>
                </p>
                <div className="mt-4 flex gap-4 text-xs font-mono">
                    <button
                        onClick={() => status === 'connected' ? disconnect() : setIsSettingsOpen(true)}
                        className="hover:underline"
                    >
                        [ {status === 'connected' ? 'disconnect' : 'connect'} ]
                    </button>
                    <button onClick={clearLogs} className="hover:underline text-gray-500">[ clear ]</button>
                </div>
            </header>

            <div className="flex-1 border border-black p-4 font-mono text-xs overflow-hidden flex flex-col bg-white">
                <div className="flex-1 overflow-y-auto space-y-1" ref={scrollRef}>
                    {logs.map((log) => (
                        <div key={log.id} className={`break-all ${log.type === 'tx' ? 'text-blue-600' :
                            log.type === 'error' ? 'text-red-600' :
                                'text-black'
                            }`}>
                            {log.type === 'tx' ? '> ' : ''}{log.content}
                        </div>
                    ))}
                </div>

                <form onSubmit={handleSend} className="mt-2 flex gap-2 border-t border-gray-200 pt-2">
                    <span className="text-gray-400">{'>'}</span>
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="flex-1 outline-none bg-transparent"
                        placeholder="Enter command..."
                        autoFocus
                    />
                </form>
            </div>

            <MinimalSettingsModal
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                onConnect={handleConnect}
                isTestMode={isTestMode}
                onTestModeChange={setIsTestMode}
            />

            {showIpConsentModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm p-4">
                    <div className="w-full max-w-sm border border-black bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-6">
                        <h3 className="text-sm font-bold uppercase tracking-widest mb-4">Permission Request</h3>
                        <p className="text-xs mb-4 text-gray-600">
                            The "abhinav" command uses your device location (or IP address) to provide a personalized greeting. This data is not stored.
                        </p>
                        <div className="flex gap-4">
                            <button
                                onClick={() => handleIpConsent(true)}
                                className="flex-1 bg-black text-white hover:bg-gray-800 font-bold uppercase tracking-wider py-2 text-xs transition-colors"
                            >
                                Allow
                            </button>
                            <button
                                onClick={() => handleIpConsent(false)}
                                className="flex-1 border border-black text-black hover:bg-gray-100 font-bold uppercase tracking-wider py-2 text-xs transition-colors"
                            >
                                Deny
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MinimalCLI;
